import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  CircleDot,
  Database,
  Loader2,
  PlayCircle,
  RefreshCw,
  Save,
  Server,
  Settings as SettingsIcon,
  ShieldCheck,
  StopCircle,
  Terminal,
  Zap,
} from 'lucide-react';
import {
  cancelSync,
  deleteConnection,
  getConnection,
  listContractEntities,
  listEntities,
  listSyncRuns,
  testConnection,
  triggerFullResync,
  triggerSyncNow,
  updateConnection,
  upsertEntity,
} from '../api/connectors';
import type {
  Connection,
  ConnectionUpdateRequest,
  ContractEntity,
  SyncEntity,
  SyncEntityRequest,
  SyncRun,
  SyncRunStatus,
  TestResult,
} from '../types/connector';

// -----------------------------------------------------------------------------
// ConnectionDetail — Story 9.9 AC2-7
//
// Hosts the four sub-tabs of a single Connection:
//  - General (AC2, AC3, AC4) — editable form + Test Connection verdict
//  - Entities (AC5) — enable/disable + schedule + sync-now + full-resync
//  - Sync History (AC6) — recent runs with entity/status filters
//  - Logs (AC7) — placeholder; wired to Loki/Grafana in Story 9.10
//
// Layout pattern copied from AlertManager / Settings to stay consistent with
// the rest of the reporting dashboard. All four tabs live in this file so a
// reviewer can follow the state flow top-to-bottom without chasing imports.
// -----------------------------------------------------------------------------

type Tab = 'general' | 'entities' | 'history' | 'logs';

function statusDotColor(status: string): string {
  switch (status) {
    case 'active':
    case 'success':
      return 'text-emerald-500';
    case 'paused':
      return 'text-amber-500';
    case 'error':
    case 'failed':
      return 'text-red-500';
    case 'running':
      return 'text-sky-500';
    case 'cancelled':
    case 'draft':
      return 'text-slate-400';
    default:
      return 'text-slate-400';
  }
}

function formatDuration(ms: number): string {
  if (!ms || ms < 1) return '—';
  if (ms < 1000) return `${ms}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  const rest = Math.round(s - m * 60);
  return `${m}m ${rest}s`;
}

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '—';
  return d.toLocaleString();
}

// -----------------------------------------------------------------------------
// Page shell
// -----------------------------------------------------------------------------

export function ConnectionDetail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();

  const [conn, setConn] = useState<Connection | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('general');

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function load() {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const env = await getConnection(id);
      setConn(env.data);
    } catch (e) {
      console.error('getConnection failed', e);
      setError(
        t('connectors.detail.loadError', 'Failed to load connection. It may have been deleted.'),
      );
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-500 dark:text-slate-400">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        {t('common.loading', 'Loading...')}
      </div>
    );
  }

  if (error || !conn) {
    return (
      <div>
        <button
          type="button"
          onClick={() => navigate('/settings/data-sources')}
          className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('connectors.detail.back', 'Back to connections')}
        </button>
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-200">
          {error}
        </div>
      </div>
    );
  }

  const tabs: Array<{ key: Tab; label: string; icon: React.ComponentType<{ className?: string }> }> = [
    { key: 'general', label: t('connectors.tabs.general', 'General'), icon: SettingsIcon },
    { key: 'entities', label: t('connectors.tabs.entities', 'Entities'), icon: Database },
    { key: 'history', label: t('connectors.tabs.history', 'Sync History'), icon: RefreshCw },
    { key: 'logs', label: t('connectors.tabs.logs', 'Logs'), icon: Terminal },
  ];

  return (
    <div>
      <button
        type="button"
        onClick={() => navigate('/settings/data-sources')}
        className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
      >
        <ArrowLeft className="h-4 w-4" />
        {t('connectors.detail.back', 'Back to connections')}
      </button>

      <div className="mb-6 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Server className="h-6 w-6 text-indigo-500" />
            {conn.name}
          </h1>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 font-mono">
            {conn.driver}://{conn.host}:{conn.port}/{conn.database}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <CircleDot className={`h-5 w-5 ${statusDotColor(conn.status)}`} />
          <span className="text-sm capitalize text-slate-700 dark:text-slate-200">
            {conn.status}
          </span>
        </div>
      </div>

      <div className="mb-4 flex gap-1 border-b border-slate-200 dark:border-slate-700">
        {tabs.map((tabDef) => {
          const Icon = tabDef.icon;
          const active = tab === tabDef.key;
          return (
            <button
              key={tabDef.key}
              type="button"
              onClick={() => setTab(tabDef.key)}
              className={`flex items-center gap-1.5 border-b-2 px-4 py-2 text-sm font-medium transition ${
                active
                  ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
              }`}
            >
              <Icon className="h-4 w-4" />
              {tabDef.label}
            </button>
          );
        })}
      </div>

      {tab === 'general' && <GeneralTab conn={conn} onRefresh={load} onDeleted={() => navigate('/settings/data-sources')} />}
      {tab === 'entities' && <EntitiesTab conn={conn} />}
      {tab === 'history' && <SyncHistoryTab conn={conn} />}
      {tab === 'logs' && <LogsTab conn={conn} />}
    </div>
  );
}

// -----------------------------------------------------------------------------
// General tab — edit form + Test Connection verdict
// -----------------------------------------------------------------------------

interface TabProps {
  conn: Connection;
}

interface GeneralTabProps extends TabProps {
  onRefresh: () => void | Promise<void>;
  onDeleted: () => void;
}

function GeneralTab({ conn, onRefresh, onDeleted }: GeneralTabProps) {
  const { t } = useTranslation();
  const [form, setForm] = useState<ConnectionUpdateRequest>({
    name: conn.name,
    host: conn.host,
    port: conn.port,
    database: conn.database,
    username: conn.username,
    password: '',
    tls_enabled: conn.tls_enabled,
    tls_skip_verify: conn.tls_skip_verify,
    status: conn.status === 'error' || conn.status === 'deleted' ? undefined : conn.status,
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveOk, setSaveOk] = useState<string | null>(null);

  useEffect(() => {
    setForm({
      name: conn.name,
      host: conn.host,
      port: conn.port,
      database: conn.database,
      username: conn.username,
      password: '',
      tls_enabled: conn.tls_enabled,
      tls_skip_verify: conn.tls_skip_verify,
      status: conn.status === 'error' || conn.status === 'deleted' ? undefined : conn.status,
    });
  }, [conn]);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);
    setSaveOk(null);
    try {
      // Do NOT send an empty password — the handler preserves the existing
      // envelope when the field is absent, but omitting it entirely makes
      // the intent clearer to any reviewer reading the wire.
      const payload: ConnectionUpdateRequest = { ...form };
      if (!payload.password) {
        delete payload.password;
      }
      await updateConnection(conn.connection_id, payload);
      setSaveOk(t('connectors.form.saveOk', 'Saved.'));
      await onRefresh();
      setForm((f) => ({ ...f, password: '' }));
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      setSaveError(msg || t('connectors.form.saveError', 'Failed to save connection.'));
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    setTesting(true);
    setTestResult(null);
    try {
      const env = await testConnection(conn.connection_id);
      setTestResult(env.data);
      // The handler also persists status changes — reload to see them.
      await onRefresh();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      setTestResult({
        reachable: false,
        ping_ms: 0,
        views_found: [],
        views_missing: [],
        column_mismatches: [],
        overall_status: 'failed',
        error: msg || t('connectors.test.unknownError', 'Unknown error'),
      });
    } finally {
      setTesting(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm(t('connectors.form.deleteConfirm', 'Delete this connection? This cannot be undone.'))) {
      return;
    }
    setDeleting(true);
    try {
      await deleteConnection(conn.connection_id);
      onDeleted();
    } catch (e) {
      console.error('deleteConnection failed', e);
      setSaveError(t('connectors.form.deleteError', 'Failed to delete connection.'));
      setDeleting(false);
    }
  }

  return (
    <div className="grid gap-6 md:grid-cols-3">
      <form onSubmit={handleSave} className="md:col-span-2 space-y-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <h2 className="text-base font-semibold text-slate-900 dark:text-white">
          {t('connectors.general.title', 'Connection settings')}
        </h2>

        {saveError && (
          <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
            {saveError}
          </div>
        )}
        {saveOk && (
          <div className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300">
            {saveOk}
          </div>
        )}

        <div>
          <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
            {t('connectors.form.name', 'Name')}
          </label>
          <input
            type="text"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
          />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
              {t('connectors.form.host', 'Host')}
            </label>
            <input
              type="text"
              required
              value={form.host}
              onChange={(e) => setForm({ ...form, host: e.target.value })}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
              {t('connectors.form.port', 'Port')}
            </label>
            <input
              type="number"
              required
              value={form.port}
              onChange={(e) => setForm({ ...form, port: Number(e.target.value) })}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
            />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
            {t('connectors.form.database', 'Database')}
          </label>
          <input
            type="text"
            required
            value={form.database}
            onChange={(e) => setForm({ ...form, database: e.target.value })}
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
              {t('connectors.form.username', 'Username')}
            </label>
            <input
              type="text"
              required
              autoComplete="off"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
              {t('connectors.form.passwordUpdate', 'Password (leave blank to keep)')}
            </label>
            <input
              type="password"
              autoComplete="new-password"
              value={form.password ?? ''}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="***"
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
            />
          </div>
        </div>

        <div className="flex items-center gap-4 text-sm text-slate-700 dark:text-slate-300">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.tls_enabled ?? true}
              onChange={(e) => setForm({ ...form, tls_enabled: e.target.checked })}
            />
            {t('connectors.form.tlsEnabled', 'TLS enabled')}
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.tls_skip_verify ?? false}
              onChange={(e) => setForm({ ...form, tls_skip_verify: e.target.checked })}
            />
            {t('connectors.form.tlsSkipVerify', 'Skip cert verify')}
          </label>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
            {t('connectors.form.status', 'Status')}
          </label>
          <select
            value={form.status ?? 'draft'}
            onChange={(e) =>
              setForm({ ...form, status: e.target.value as ConnectionUpdateRequest['status'] })
            }
            className="w-48 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
          >
            <option value="draft">draft</option>
            <option value="active">active</option>
            <option value="paused">paused</option>
          </select>
        </div>

        <div className="flex items-center justify-between pt-3">
          <button
            type="button"
            onClick={() => void handleDelete()}
            disabled={deleting}
            className="rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 dark:border-red-900/40 dark:bg-slate-800 dark:text-red-400 dark:hover:bg-red-900/20"
          >
            {deleting
              ? t('common.deleting', 'Deleting...')
              : t('connectors.form.deleteButton', 'Delete connection')}
          </button>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
          >
            <Save className="h-4 w-4" />
            {saving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
          </button>
        </div>
      </form>

      <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-emerald-500" />
          <h2 className="text-base font-semibold text-slate-900 dark:text-white">
            {t('connectors.test.title', 'Test connection')}
          </h2>
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {t(
            'connectors.test.subtitle',
            'Runs a ping + contract compatibility check against the source database using the currently-saved credentials.',
          )}
        </p>
        <button
          type="button"
          disabled={testing}
          onClick={() => void handleTest()}
          className="inline-flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-100 dark:border-indigo-900/50 dark:bg-indigo-900/20 dark:text-indigo-300"
        >
          {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
          {testing
            ? t('connectors.test.running', 'Testing...')
            : t('connectors.test.button', 'Run test')}
        </button>

        {testResult && (
          <div className="mt-3 space-y-2 rounded-lg bg-slate-50 p-3 text-xs dark:bg-slate-900/50">
            <div className="flex items-center gap-2">
              <CircleDot className={`h-4 w-4 ${statusDotColor(testResult.overall_status === 'ok' ? 'active' : testResult.overall_status === 'partial' ? 'paused' : 'error')}`} />
              <span className="font-semibold capitalize text-slate-800 dark:text-slate-100">
                {testResult.overall_status}
              </span>
              {testResult.reachable && (
                <span className="text-slate-500 dark:text-slate-400">
                  ({testResult.ping_ms}ms)
                </span>
              )}
            </div>
            {testResult.error && (
              <div className="text-red-600 dark:text-red-400">{testResult.error}</div>
            )}
            {testResult.hint && (
              <div className="italic text-slate-500 dark:text-slate-400">{testResult.hint}</div>
            )}
            {testResult.views_found.length > 0 && (
              <div>
                <div className="font-medium text-slate-700 dark:text-slate-200">
                  {t('connectors.test.viewsFound', 'Views found')} ({testResult.views_found.length})
                </div>
                <ul className="mt-1 list-inside list-disc text-slate-600 dark:text-slate-300">
                  {testResult.views_found.map((v) => (
                    <li key={v}>{v}</li>
                  ))}
                </ul>
              </div>
            )}
            {testResult.views_missing.length > 0 && (
              <div>
                <div className="font-medium text-amber-700 dark:text-amber-300">
                  {t('connectors.test.viewsMissing', 'Views missing')} ({testResult.views_missing.length})
                </div>
                <ul className="mt-1 list-inside list-disc text-amber-600 dark:text-amber-300">
                  {testResult.views_missing.map((v) => (
                    <li key={v}>{v}</li>
                  ))}
                </ul>
              </div>
            )}
            {testResult.column_mismatches.length > 0 && (
              <div>
                <div className="font-medium text-red-700 dark:text-red-300">
                  {t('connectors.test.columnDrift', 'Column drift')} ({testResult.column_mismatches.length})
                </div>
                <ul className="mt-1 space-y-1 text-red-600 dark:text-red-300">
                  {testResult.column_mismatches.map((c, i) => (
                    <li key={`${c.entity}.${c.column}.${i}`}>
                      <code>{c.entity}.{c.column}</code> — {c.reason}
                      {c.got ? ` (got ${c.got}, expected ${c.expected})` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Entities tab — list from contract, enable/disable + schedule + sync-now
// -----------------------------------------------------------------------------

const SCHEDULE_OPTIONS = [
  { value: '*/5 * * * *', label: 'Every 5 minutes' },
  { value: '*/15 * * * *', label: 'Every 15 minutes' },
  { value: '0 * * * *', label: 'Every hour' },
  { value: 'manual', label: 'Manual only' },
];

function EntitiesTab({ conn }: TabProps) {
  const { t } = useTranslation();
  const [contractEntities, setContractEntities] = useState<ContractEntity[]>([]);
  const [entities, setEntities] = useState<SyncEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conn.connection_id]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [contractEnv, entitiesEnv] = await Promise.all([
        listContractEntities(),
        listEntities(conn.connection_id).catch((e: unknown) => {
          // 501 Not Implemented is non-fatal — the worker may not be wired.
          const status = (e as { response?: { status?: number } })?.response?.status;
          if (status === 501) return { data: [] as SyncEntity[] };
          throw e;
        }),
      ]);
      setContractEntities(contractEnv.data.entities ?? []);
      setEntities(entitiesEnv.data ?? []);
    } catch (e) {
      console.error('entities load failed', e);
      setError(t('connectors.entities.loadError', 'Failed to load entities.'));
    } finally {
      setLoading(false);
    }
  }

  function entityRow(name: string): SyncEntity | undefined {
    return entities.find((e) => e.entity === name);
  }

  async function handleUpsert(entity: string, body: SyncEntityRequest) {
    setWorking(entity);
    setError(null);
    try {
      const env = await upsertEntity(conn.connection_id, entity, body);
      setEntities((prev) => {
        const others = prev.filter((p) => p.entity !== entity);
        return [...others, env.data];
      });
      setNotice(t('connectors.entities.saved', 'Entity saved.'));
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      setError(msg || t('connectors.entities.saveError', 'Failed to save entity.'));
    } finally {
      setWorking(null);
    }
  }

  async function handleSyncNow(entity: string) {
    setWorking(entity);
    setError(null);
    try {
      await triggerSyncNow(conn.connection_id, entity);
      setNotice(t('connectors.entities.syncQueued', 'Sync queued.'));
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        setError(t('connectors.entities.alreadyRunning', 'A sync is already running for this entity.'));
      } else if (status === 501) {
        setError(t('connectors.entities.triggerUnwired', 'sync-now is not available on this binary.'));
      } else {
        const msg = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
        setError(msg || t('connectors.entities.syncError', 'Failed to queue sync.'));
      }
    } finally {
      setWorking(null);
    }
  }

  async function handleFullResync(entity: string) {
    if (!window.confirm(t('connectors.entities.fullResyncConfirm', 'Reset the cursor and replay this entity from scratch?'))) {
      return;
    }
    setWorking(entity);
    setError(null);
    try {
      await triggerFullResync(conn.connection_id, entity);
      setNotice(t('connectors.entities.fullResyncQueued', 'Full resync queued.'));
      await load();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      setError(msg || t('connectors.entities.fullResyncError', 'Failed to queue full resync.'));
    } finally {
      setWorking(null);
    }
  }

  async function handleCancel(entity: string) {
    setWorking(entity);
    setError(null);
    try {
      await cancelSync(conn.connection_id, entity);
      setNotice(t('connectors.entities.cancelQueued', 'Cancel requested.'));
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      setError(msg || t('connectors.entities.cancelError', 'Failed to request cancel.'));
    } finally {
      setWorking(null);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-slate-500 dark:text-slate-400">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        {t('common.loading', 'Loading...')}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </div>
      )}
      {notice && (
        <div className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300">
          {notice}
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
          <thead className="bg-slate-50 dark:bg-slate-900/50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                {t('connectors.entities.columns.entity', 'Entity')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                {t('connectors.entities.columns.enabled', 'Enabled')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                {t('connectors.entities.columns.schedule', 'Schedule')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                {t('connectors.entities.columns.cursor', 'Cursor')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                {t('connectors.entities.columns.lastSync', 'Last sync')}
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                {t('connectors.entities.columns.actions', 'Actions')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {contractEntities.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                  {t('connectors.entities.noContract', 'Data Source Contract is empty.')}
                </td>
              </tr>
            ) : (
              contractEntities.map((ce) => {
                const row = entityRow(ce.name);
                const enabled = row?.enabled ?? false;
                const schedule = row?.schedule_cron ?? '*/15 * * * *';
                const busy = working === ce.name;

                return (
                  <tr key={ce.name} className="hover:bg-slate-50 dark:hover:bg-slate-900/50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900 dark:text-white">{ce.name}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400 line-clamp-1 max-w-md">
                        {ce.description}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <label className="inline-flex cursor-pointer items-center gap-2">
                        <input
                          type="checkbox"
                          checked={enabled}
                          disabled={busy}
                          onChange={(e) =>
                            void handleUpsert(ce.name, {
                              enabled: e.target.checked,
                              schedule_cron: schedule,
                            })
                          }
                        />
                        <span className="text-sm text-slate-700 dark:text-slate-200">
                          {enabled ? t('common.enabled', 'Enabled') : t('common.disabled', 'Disabled')}
                        </span>
                      </label>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={schedule}
                        disabled={busy}
                        onChange={(e) =>
                          void handleUpsert(ce.name, {
                            enabled,
                            schedule_cron: e.target.value,
                          })
                        }
                        className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                      >
                        {SCHEDULE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500 dark:text-slate-400">
                      {row?.cursor_value ?? 0}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500 dark:text-slate-400">
                      {formatDate(row?.last_sync_at)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void handleSyncNow(ce.name)}
                          className="inline-flex items-center gap-1 rounded border border-indigo-200 bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100 disabled:opacity-60 dark:border-indigo-900/50 dark:bg-indigo-900/20 dark:text-indigo-300"
                        >
                          <PlayCircle className="h-3 w-3" />
                          {t('connectors.entities.syncNow', 'Sync now')}
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void handleFullResync(ce.name)}
                          className="inline-flex items-center gap-1 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700 hover:bg-amber-100 disabled:opacity-60 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-300"
                        >
                          <RefreshCw className="h-3 w-3" />
                          {t('connectors.entities.fullResync', 'Full')}
                        </button>
                        {row?.cancel_requested ? (
                          <span className="text-xs italic text-slate-400">
                            {t('connectors.entities.cancelPending', 'cancel pending')}
                          </span>
                        ) : (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => void handleCancel(ce.name)}
                            className="inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-60 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300"
                          >
                            <StopCircle className="h-3 w-3" />
                            {t('connectors.entities.cancel', 'Cancel')}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Sync History tab — recent runs with filters + expandable error row
// -----------------------------------------------------------------------------

function SyncHistoryTab({ conn }: TabProps) {
  const { t } = useTranslation();
  const [runs, setRuns] = useState<SyncRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [entityFilter, setEntityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | SyncRunStatus>('');
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conn.connection_id]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const env = await listSyncRuns(conn.connection_id, { limit: 100 });
      setRuns(env.data ?? []);
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status === 501) {
        setError(t('connectors.history.unwired', 'Sync history is not wired on this binary.'));
      } else {
        setError(t('connectors.history.loadError', 'Failed to load sync runs.'));
      }
    } finally {
      setLoading(false);
    }
  }

  const filtered = useMemo(() => {
    return runs.filter((r) => {
      if (entityFilter && r.entity !== entityFilter) return false;
      if (statusFilter && r.status !== statusFilter) return false;
      return true;
    });
  }, [runs, entityFilter, statusFilter]);

  const uniqueEntities = useMemo(() => Array.from(new Set(runs.map((r) => r.entity))), [runs]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-xs text-slate-500 dark:text-slate-400">
          {t('connectors.history.filterEntity', 'Entity')}:
        </label>
        <select
          value={entityFilter}
          onChange={(e) => setEntityFilter(e.target.value)}
          className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
        >
          <option value="">{t('common.all', 'All')}</option>
          {uniqueEntities.map((e) => (
            <option key={e} value={e}>
              {e}
            </option>
          ))}
        </select>
        <label className="text-xs text-slate-500 dark:text-slate-400">
          {t('connectors.history.filterStatus', 'Status')}:
        </label>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as '' | SyncRunStatus)}
          className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
        >
          <option value="">{t('common.all', 'All')}</option>
          <option value="running">running</option>
          <option value="success">success</option>
          <option value="failed">failed</option>
          <option value="cancelled">cancelled</option>
        </select>
        <button
          type="button"
          onClick={() => void load()}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
        >
          <RefreshCw className="h-4 w-4" />
          {t('common.refresh', 'Refresh')}
        </button>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
          <thead className="bg-slate-50 dark:bg-slate-900/50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                {t('connectors.history.columns.startedAt', 'Started')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                {t('connectors.history.columns.entity', 'Entity')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                {t('connectors.history.columns.trigger', 'Trigger')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                {t('connectors.history.columns.status', 'Status')}
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                {t('connectors.history.columns.rowsInserted', 'Inserted')}
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                {t('connectors.history.columns.rowsFailed', 'Failed')}
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                {t('connectors.history.columns.duration', 'Duration')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                  {t('common.loading', 'Loading...')}
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                  {t('connectors.history.empty', 'No sync runs in this window.')}
                </td>
              </tr>
            ) : (
              filtered.map((r) => {
                const isOpen = expanded === r.run_id;
                const hasError = !!r.error_message;
                return (
                  <React.Fragment key={r.run_id}>
                    <tr
                      className={`cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-900/50 ${
                        hasError ? '' : ''
                      }`}
                      onClick={() => hasError && setExpanded(isOpen ? null : r.run_id)}
                    >
                      <td className="whitespace-nowrap px-4 py-2 text-xs text-slate-600 dark:text-slate-300">
                        {formatDate(r.started_at)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-xs font-mono text-slate-700 dark:text-slate-200">
                        {r.entity}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-xs text-slate-500 dark:text-slate-400">
                        {r.trigger}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-xs">
                        <span className="inline-flex items-center gap-1">
                          <CircleDot className={`h-3 w-3 ${statusDotColor(r.status)}`} />
                          <span className="capitalize text-slate-700 dark:text-slate-200">
                            {r.status}
                          </span>
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-right text-xs text-slate-700 dark:text-slate-200">
                        {r.rows_inserted.toLocaleString()}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-right text-xs text-slate-700 dark:text-slate-200">
                        {r.rows_failed.toLocaleString()}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-right text-xs text-slate-500 dark:text-slate-400">
                        {formatDuration(r.duration_ms)}
                      </td>
                    </tr>
                    {isOpen && hasError && (
                      <tr className="bg-red-50/40 dark:bg-red-900/10">
                        <td colSpan={7} className="px-4 py-2 text-xs text-red-700 dark:text-red-300">
                          <strong>{t('connectors.history.errorLabel', 'Error')}:</strong>{' '}
                          <code className="font-mono">{r.error_message}</code>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Logs tab — placeholder. Story 9.10 wires this to Loki/Grafana.
// -----------------------------------------------------------------------------

function LogsTab({ conn }: TabProps) {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center dark:border-slate-700 dark:bg-slate-800">
      <Terminal className="mx-auto h-10 w-10 text-slate-300 dark:text-slate-600" />
      <h2 className="mt-3 text-base font-semibold text-slate-900 dark:text-white">
        {t('connectors.logs.title', 'Live logs')}
      </h2>
      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">
        {t(
          'connectors.logs.placeholder',
          'Streaming logs for this connection will land here once Story 9.10 wires the worker log tail to the dashboard.',
        )}
      </p>
      <p className="mt-3 font-mono text-xs text-slate-400">
        connection_id={conn.connection_id}
      </p>
    </div>
  );
}

export default ConnectionDetail;
